import defaultImage from "../assets/field.jpg";

export const safeArr = (v) => (Array.isArray(v) ? v : []);

export const slugify = (value = "") =>
  String(value || "")
    .replace(/İ/g, "i")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase("tr-TR")
    .replace(/ç/g, "c")
    .replace(/ğ/g, "g")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ş/g, "s")
    .replace(/ü/g, "u")
    .replace(/[^a-z0-9 ]/g, "")
    .trim()
    .replace(/\s+/g, "-");

export const encodeSegment = (segment = "") => encodeURIComponent(String(segment || ""));

export const formatDate = (value) => {
  if (!value) return "Tarih bekleniyor";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("tr-TR", { day: "numeric", month: "short", weekday: "short" });
};

export const formatTime = (value) => {
  if (!value) return "Saat bekleniyor";
  return String(value).slice(0, 5);
};

export const parseScore = (value) => {
  const score = Number(value);
  return Number.isFinite(score) ? score : null;
};

export const parseManualPoints = (value) => {
  const points = Number(value);
  return Number.isFinite(points) ? points : 0;
};

export const toBool = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  if (value === 1 || value === "1") return true;
  if (value === 0 || value === "0") return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLocaleLowerCase("tr-TR");
    if (["true", "aktif", "active", "evet", "yes"].includes(normalized)) return true;
    if (["false", "pasif", "inactive", "hayir", "hayır", "no"].includes(normalized)) return false;
  }
  return Boolean(value);
};

const coerceActiveState = (value) => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  if (typeof value === "string") {
    const normalized = value.trim().toLocaleLowerCase("tr-TR");
    if (!normalized) return undefined;
    if (["false", "0", "pasif", "inactive", "kapalı", "hayır", "hayir", "no"].includes(normalized))
      return false;
    if (["true", "1", "aktif", "active", "açık", "acik", "evet", "yes"].includes(normalized))
      return true;
  }
  return Boolean(value);
};

export const isEntityActive = (entity) => {
  if (!entity) return true;
  const activeKeys = [
    "active",
    "is_active",
    "isActive",
    "status",
    "entity_status",
    "team_status",
    "player_status",
    "durum",
    "state",
  ];

  for (const key of activeKeys) {
    const state = coerceActiveState(entity?.[key]);
    if (state !== undefined) return state;
  }

  return true;
};

export const isTeamActive = (team) => isEntityActive(team);
export const isPlayerActive = (player) => isEntityActive(player);

export const formatCityTag = (value) => (value ? String(value).toLocaleUpperCase("tr-TR") : "");

const capitalizeSegment = (segment) => {
  const lower = segment.toLocaleLowerCase("tr-TR");
  const firstChar = lower.charAt(0);
  if (!firstChar) return lower;
  return firstChar.toLocaleUpperCase("tr-TR") + lower.slice(1);
};

export const formatLeagueSeasonTag = (value) => {
  if (!value) return "";
  return String(value)
    .split(/\s+/)
    .filter(Boolean)
    .map(capitalizeSegment)
    .join(" ");
};

export const NEUTRAL_TONE = { chip: "", surface: "alt-surface--neutral", accent: "#2563eb" };

export const CITY_TONE_PALETTE = [
  { chip: "alt-chip--tone-0", surface: "alt-surface--tone-0", accent: "#2563eb" },
  { chip: "alt-chip--tone-1", surface: "alt-surface--tone-1", accent: "#0ea5e9" },
  { chip: "alt-chip--tone-2", surface: "alt-surface--tone-2", accent: "#10b981" },
  { chip: "alt-chip--tone-3", surface: "alt-surface--tone-3", accent: "#f97316" },
  { chip: "alt-chip--tone-4", surface: "alt-surface--tone-4", accent: "#ec4899" },
  { chip: "alt-chip--tone-5", surface: "alt-surface--tone-5", accent: "#8b5cf6" },
  { chip: "alt-chip--tone-6", surface: "alt-surface--tone-6", accent: "#14b8a6" },
  { chip: "alt-chip--tone-7", surface: "alt-surface--tone-7", accent: "#f59e0b" },
];

export const ALL_OPTION = { slug: "all", label: "Tümü" };
export const FILTER_STORAGE_KEY = "elitlig.altHomeFilters";
const isBrowser = typeof window !== "undefined";

export const readStoredFilters = () => {
  if (!isBrowser) return {};
  try {
    const rawValue = window.localStorage.getItem(FILTER_STORAGE_KEY);
    if (!rawValue) return {};
    const parsed = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object") return {};
    return parsed;
  } catch {
    return {};
  }
};

export const withAllOption = (options) => [ALL_OPTION, ...options];

export const buildScopeLabel = ({
  selectedCity,
  selectedLeague,
  selectedSeason,
  cityOptions,
  leagueOptions,
  seasonOptions,
}) => {
  const cityTitle = cityOptions.find((o) => o.slug === selectedCity)?.label || "";
  const leagueTitle = leagueOptions.find((o) => o.slug === selectedLeague)?.label || "";
  const seasonTitle = seasonOptions.find((o) => o.slug === selectedSeason)?.label || "";

  if (selectedCity === ALL_OPTION.slug) return "Türkiye Geneli";
  if (selectedLeague === ALL_OPTION.slug) return `${cityTitle} Geneli`;
  if (selectedSeason === ALL_OPTION.slug) return `${cityTitle} - ${formatLeagueSeasonTag(leagueTitle)}`;
  return `${cityTitle} - ${formatLeagueSeasonTag(leagueTitle)} - ${formatLeagueSeasonTag(seasonTitle)}`;
};

export const DEFAULT_SLIDE_IMAGE = defaultImage;

const normalizeMatchText = (value) =>
  String(value || "")
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/İ/g, "i");

const resolveMatchStatus = (match) =>
  normalizeMatchText(match?.mac_durumu || match?.match_status || match?.status);

export const isMatchDraftLike = (match) => {
  const status = resolveMatchStatus(match);
  if (!status) return false;
  if (status.includes("taslak") || status.includes("silin")) return true;
  return ["draft", "taslak", "silindi", "deleted", "removed"].includes(status);
};

export const isMatchScheduledLike = (match) => {
  const status = resolveMatchStatus(match);
  if (!status) return false;
  if (status.includes("zaman")) return true;
  return ["scheduled", "schedule", "fixture", "upcoming", "planlandi", "planlandı"].includes(status);
};

export const isMatchCountable = (match) => {
  if (!match) return false;

  if (isMatchDraftLike(match)) return false;
  if (toBool(match.is_it_fixture)) return false;
  if (isMatchScheduledLike(match)) return false;

  return parseScore(match?.first_team_score) !== null && parseScore(match?.second_team_score) !== null;
};

const resolveRunningValue = (value) => {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0;
  const normalized = normalizeMatchText(value);
  if (!normalized) return false;
  return ["true", "1", "running", "aktif", "canli", "canlı"].includes(normalized);
};

export const resolveLiveStatus = (match) => {
  if (!match) return { isLive: false, isFinished: false };

  const status = normalizeMatchText(match.match_status || match.status);
  const period = normalizeMatchText(match.live_period);
  const timerState = normalizeMatchText(match.live_timer_state);
  const liveClock = normalizeMatchText(match.live_time);

  const isFinished =
    status === "finished" ||
    timerState === "finished" ||
    period.includes("bitti") ||
    period.includes("mac sonu") ||
    period.includes("maç sonu");

  const isLive =
    !isFinished &&
    (status === "live" ||
      timerState === "running" ||
      resolveRunningValue(match.live_timer_running) ||
      period.includes("canli") ||
      period.includes("canlı") ||
      Boolean(liveClock));

  return { isLive, isFinished };
};
